import React, { useEffect, useMemo } from 'react';
import { Tabs, Tab } from '@mui/material';
import PageHeaderTabs from '../../../../components/shared/page-header-tabs';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../../../hooks/hooks';
import { getVisibleProfileTabs, ProfileTabKey } from '../../tabs';

interface ProfileHeaderProps {
    activeTab: ProfileTabKey;
    actions?: React.ReactNode;
    loading?: boolean;
}

export const ProfileHeader: React.FC<ProfileHeaderProps> = ({ activeTab, actions, loading }) => {
    const profile = useAppSelector(store => store.profile);
    const navigate = useNavigate();

    const availableTabs = useMemo(
        () => getVisibleProfileTabs(profile),
        [profile],
    );

    useEffect(() => {
        if (availableTabs.length === 0) {
            return;
        }

        const currentTabExists = availableTabs.some(tab => tab.key === activeTab);
        if (!currentTabExists) {
            navigate(availableTabs[0].route, { replace: true });
        }
    }, [availableTabs, activeTab, navigate]);

    const activeTabIndex = activeTab ? availableTabs.findIndex(tab => tab.key === activeTab) : 0;
    const value = activeTabIndex >= 0 ? activeTabIndex : 0;

    const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
        const targetTab = availableTabs[newValue];
        if (targetTab) {
            navigate(targetTab.route);
        }
    };

    if (availableTabs.length === 0) {
        return <PageHeaderTabs headerLocalizationId='profile' loading={loading}>{null}</PageHeaderTabs>;
    }

    return (
        <PageHeaderTabs headerLocalizationId='profile' actions={actions} loading={loading}>
            <Tabs
                value={value}
                onChange={handleChange}
                variant='scrollable'
                scrollButtons='auto'>
                {availableTabs.map((tab, index) => (
                    <Tab
                        disableRipple
                        label={tab.name}
                        key={tab.key}
                        value={index}
                        sx={{
                            paddingTop: '12px',
                            paddingBottom: '12px',
                            paddingLeft: 0,
                            paddingRight: 0,
                            fontSize: '16px',
                            lineHeight: '24px',
                            minWidth: 0,
                            color: 'var(--text-primary)',
                            '&.Mui-selected': {
                                color: 'var(--text-primary)',
                            },
                        }}
                    />
                ))}
            </Tabs>
        </PageHeaderTabs>
    );
};

export default ProfileHeader;
